/* @flow */
import { once } from 'lodash';
import * as React from 'react';
import Button from 'components/ButtonDeprecated';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import GroupMessageListModal from 'groups/components/GroupMessageListModal';
import Link from 'components/Link';
import ModalTrigger from 'components/ModalTrigger';
import Row from 'components/Row';
import ScrollWatch from 'components/ScrollWatch';
import Text from 'components/Text';
import TextEntities from 'lib/TextEntities';
import TextWithEntities from 'components/TextWithEntities';
import type { SequenceItem } from '../chunker';

import './styles/SystemMessageItems.scss';

type Props = {
  items: Array<SequenceItem>,
};

function getModalForURL(url) {
  const urlParts = url.split('/');

  if (urlParts[3] === 'classes' && urlParts[5] === 'history') {
    return <GroupMessageListModal groupCode={urlParts[4]} />;
  }

  return null;
}

function linkRenderer(url, content) {
  return (
    <Link href={url} target="_blank">
      {content}
    </Link>
  );
}

function modalRenderer(modal, content) {
  return (
    <ModalTrigger modal={modal}>
      <Link>{content}</Link>
    </ModalTrigger>
  );
}

function attributeRenderer(text, rendered, data) {
  const modal = getModalForURL(data.url);

  if (modal) {
    return modalRenderer(modal, rendered);
  }

  if (data.url) {
    return linkRenderer(data.url, rendered);
  }

  return rendered;
}

class SystemMessageItems extends React.Component<Props, void> {
  trackView = once(() => {
    EventTracking.trackKey('text_after_call.contact_card.view');
  });

  renderAttachment = (attachment: *) => {
    const { contentType } = attachment;
    if (!contentType || !contentType.includes('text/vcard')) {
      return null;
    }

    return (
      <ScrollWatch onEnter={() => this.trackView()}>
        <div>
          <Button
            level="text"
            href={attachment.url}
            download
            eventKey="text_after_call.contact_card.click">
            <div className="MessageSystemItem-attachment">
              <Row size="xsmall">
                <Glyph name="contact" color="pool" size="xxlarge" />
              </Row>
              <Text size="xsmall">{attachment.name}</Text>
            </div>
          </Button>
        </div>
      </ScrollWatch>
    );
  };

  renderItem = (item: SequenceItem) => {
    const systemMessageItem = item.item;
    if (systemMessageItem.__typename !== 'SystemMessageItem') {
      return (
        <ErrorReporter error="SystemMessageItems - tried to render with non-system-messages" />
      );
    }
    const { body, uuid, files, bodyAttributes } = systemMessageItem;

    if (!body) {
      return null;
    }

    const entities = [].concat(
      TextEntities.parseEmoji(body),
      TextEntities.parseNewlines(body),
      TextEntities.parseLeadingWhitespace(body),
      TextEntities.parseUrls(body),
      TextEntities.parseAttributes(bodyAttributes ? [...bodyAttributes] : [], attributeRenderer)
    );

    return (
      <div key={uuid} className="SystemMessageItems-item">
        <TextWithEntities text={body} entities={entities} />
        {files && files.length ? this.renderAttachment(files[0]) : null}
      </div>
    );
  };

  render() {
    if (this.props.items.length <= 0) {
      return null;
    }

    return <div className="SystemMessageItems">{this.props.items.map(this.renderItem)}</div>;
  }
}

export default SystemMessageItems;
